import React, { useEffect, useMemo, useState } from 'react';
import { Label, LabelGroup } from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';
import { withErrorBoundary } from 'react-error-boundary';
import classNames from 'classnames';
import { OfferingInfoBox, OfferingStats } from '@ole-ui/ole-ui-components';
import {
  CatalogEntry,
  CourseMetadata as CourseMetadataType,
  VocabularyItem,
  VocabularyNamespaces,
} from 'types';

import LessonIndicators from 'components/Catalog/Card/Lesson/LessonIndicators';
import ComponentError from 'components/Error/ComponentError';

import useMobxStores from 'hooks/useMobxStores';
import { getLanguageInfo } from 'services/LanguageService';
import { handleError } from 'services/ErrorService';

import RelatedContent from './RelatedContent';

import './styles.scss';

type Props = {
  courseCatalogEntry?: CatalogEntry;
  metadata?: CourseMetadataType;
  currentLanguage?: string;
  asCollateral?: boolean;
  languages?: string[];
};

const getLocalizedField = (
  field: Record<string, string> | undefined,
  currentLanguage: string,
): string | undefined => {
  if (!field) return undefined;

  // eslint-disable-next-line @typescript-eslint/dot-notation
  return field[currentLanguage] || field['en-US'] || field['en'];
};

const OverviewTab = ({
  courseCatalogEntry,
  metadata,
  languages,
  currentLanguage = 'en-US',
  asCollateral = false,
}: Props) => {
  const { t } = useTranslation();
  const { vocabularyStore } = useMobxStores();
  const [productVocabulary, setProductVocabulary] = useState<VocabularyItem[]>(
    [],
  );

  const description = useMemo(
    () => getLocalizedField(metadata?.description, currentLanguage),
    [metadata, currentLanguage],
  );

  const objectives = useMemo(
    () => getLocalizedField(metadata?.objectives, currentLanguage),
    [metadata, currentLanguage],
  );

  const prerequisites = useMemo(
    () => getLocalizedField(metadata?.prerequisites, currentLanguage),
    [metadata, currentLanguage],
  );

  const products = useMemo(() => {
    if (metadata && metadata.taxonomies?.products && productVocabulary) {
      const labels = metadata.taxonomies.products.map((p) => {
        const vocabLabel = productVocabulary.find((v) => v.token === p);

        if (vocabLabel) {
          return vocabLabel.display_name;
        }

        return '';
      });

      return labels.join(', ');
    }

    return null;
  }, [metadata, currentLanguage, productVocabulary]);

  const entryTags = useMemo(() => {
    if (courseCatalogEntry && courseCatalogEntry.categories) {
      return (
        <LabelGroup numLabels={10}>
          {courseCatalogEntry.categories.map((category) => (
            <Label key={category} color="blue">
              {category}
            </Label>
          ))}
        </LabelGroup>
      );
    }

    return null;
  }, [courseCatalogEntry]);

  const headingClasses = classNames('course-overview__h3', {
    'course-overview__h3--dark': asCollateral,
  });

  const IndicatorWrapperElem = asCollateral ? 'div' : OfferingInfoBox;

  useEffect(() => {
    // loads vocabulary for product whenever metadata language changes
    const fetchVocabulary = async () => {
      try {
        const voc = await vocabularyStore.getVocabularyByNamespace(
          VocabularyNamespaces.OfferingTaxonomyProducts,
          currentLanguage,
        );

        if (Array.isArray(voc)) {
          setProductVocabulary(voc);
        }
      } catch (e) {
        console.error('Error fetching product vocabulary for metadata', e);
      }
    };

    fetchVocabulary();
  }, [currentLanguage]);

  return (
    <div className="course-overview">
      {metadata && (
        <IndicatorWrapperElem tags={entryTags}>
          <LessonIndicators
            includeLabels
            disableLegend
            key="lesson-indicator"
            estimatedTime={metadata.total_duration}
            GECount={metadata.total_ge}
            quizCount={metadata.total_quiz}
            labCount={metadata.total_labs}
            level={metadata.level}
            videoTime={metadata.total_video_duration}
            durationUnit={metadata.duration_unit}
            product={asCollateral ? undefined : products}
            // competencies={metadata.skills?.join?.(', ')} // TODO: After MVP only
            // associatedCertifications={metadata.certifications} // TODO: After MVP only
          />
        </IndicatorWrapperElem>
      )}

      {metadata && currentLanguage && (
        <>
          {description && (
            <>
              <h3 className={headingClasses}>{t('Abstract')}</h3>
              {description}
            </>
          )}

          {objectives && objectives.length > 0 && (
            <>
              <h3 className={headingClasses}>{t('Objectives')}</h3>
              <ul className="course-overview__list">
                {objectives.map((objective) => (
                  <li key={objective}>{objective}</li>
                ))}
              </ul>
            </>
          )}

          {asCollateral && products && products.length > 0 && (
            <>
              <h3 className={headingClasses}>{t('Tags')}</h3>
              <div className="course-overview__collateral-tags">
                <OfferingStats.Item label={t('Product:')} icon={null}>
                  {products}
                </OfferingStats.Item>
              </div>
            </>
          )}

          {prerequisites?.length > 0 && (
            <>
              <h3 className={headingClasses}>{t('Prerequisites')}</h3>
              <ul className="course-overview__list">
                {prerequisites.map((prerequisite) => (
                  <li key={prerequisite}>{prerequisite}</li>
                ))}
              </ul>
            </>
          )}

          <RelatedContent
            relatedContent={metadata.related_contents}
            heading={
              <h3 className={headingClasses}>
                {t('This lesson is also a part of the following courses:')}
              </h3>
            }
          />
        </>
      )}

      {languages?.length > 0 && (
        <>
          <h3 className={headingClasses}>{t('Available languages')}</h3>
          <div>
            {languages?.map?.((lang, index) => {
              const localName = getLanguageInfo(lang)?.localName || lang;
              const isLast = index === languages.length - 1;

              return (
                <span key={lang}>
                  {localName}
                  {!isLast && ', '}
                </span>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default withErrorBoundary(OverviewTab, ComponentError, handleError);
